import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '@/config/firebase'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { useAuthStore } from '@/store/authStore'
import Layout from '@/components/Layout'
import LoginPage from '@/pages/LoginPage'
import SignUpPage from '@/pages/SignUpPage'
import OnboardingPage from '@/pages/OnboardingPage'
import CreateOrgPage from '@/pages/CreateOrgPage'
import JoinOrgPage from '@/pages/JoinOrgPage'
import DashboardPage from '@/pages/DashboardPage'
import SpacesPage from '@/pages/SpacesPage'
import BookingsPage from '@/pages/BookingsPage'
import BookingCatalog from '@/components/Bookings/BookingCatalog'
import NewBookingPage from '@/pages/NewBookingPage'
import ApprovalQueuePage from '@/pages/ApprovalQueuePage'
import PaymentQueuePage from '@/pages/PaymentQueuePage'
import BookingDetailsPage from '@/pages/BookingDetailsPage'
import AdminPage from '@/pages/AdminPage'
import MembersPage from '@/pages/MembersPage'
import ReportsPage from '@/pages/ReportsPage'
import FinancialDashboardPage from '@/pages/FinancialDashboardPage'

import { UserProfilePage } from '@/pages/UserProfilePage'
import { CommunicationPage } from '@/pages/CommunicationPage'
import LandingPage from './pages/LandingPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import BookingFilterPage from '@/pages/BookingFilterPage'
import UpgradePlanPage from '@/pages/UpgradePlanPage'
import BookingUnifiedPage from '@/pages/BookingUnifiedPage'
import AdminBookingsPage from '@/pages/AdminBookingsPage'
import { Organization } from '@/types'
import { NotificationProvider } from '@/contexts/NotificationContext'
import { Toaster } from 'react-hot-toast'
import ReloadPrompt from '@/components/ReloadPrompt'

// Componente de proteção de rotas
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore()
  if (loading) return null
  return user ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
  const {
    user,
    setUser,
    setUserData,
    setUserRole,
    setOrganizations,
    setCurrentOrg,
    setLoading,
    loading,
    currentOrg
  } = useAuthStore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setLoading(true)
      if (authUser) {
        setUser(authUser)

        // Carregar dados extras do usuário
        const userDoc = await getDoc(doc(db, 'users', authUser.uid))
        if (userDoc.exists()) {

          const data = userDoc.data()
          setUserData({
            id: authUser.uid,
            displayName: data.displayName || data.name || '',
            email: data.email || '',
            photoURL: data.photoURL || '',
            createdAt: data.createdAt,
            ...data // Spread remaining fields (cpf, address, etc)
          } as any)
        }

        // Carregar organizações as quais o usuário pertence
        const membersQuery = query(
          collection(db, 'members'),
          where('userId', '==', authUser.uid)
        )
        const membersSnapshot = await getDocs(membersQuery)
        const orgIds = membersSnapshot.docs.map(doc => doc.data().orgId)

        // Também incluir organizações que ele é dono
        const ownedQuery = query(
          collection(db, 'organizations'),
          where('ownerId', '==', authUser.uid)
        )
        const ownedSnapshot = await getDocs(ownedQuery)
        const ownedIds = ownedSnapshot.docs.map(doc => doc.id)

        const uniqueOrgIds = Array.from(new Set([...orgIds, ...ownedIds]))

        if (uniqueOrgIds.length > 0) {
          const orgsData: Organization[] = []
          for (const id of uniqueOrgIds) {
            const orgDoc = await getDoc(doc(db, 'organizations', id))
            if (orgDoc.exists()) {
              orgsData.push({ id: orgDoc.id, ...orgDoc.data() } as Organization)
            }
          }
          setOrganizations(orgsData)
          localStorage.setItem('spacerub_cached_orgs', JSON.stringify(orgsData))

          // Tentar restaurar a organização selecionada
          const savedOrgId = localStorage.getItem('spacerub_current_org_id')
          const savedOrg = orgsData.find(o => o.id === savedOrgId)
          const activeOrg = savedOrg || orgsData[0]
          setCurrentOrg(activeOrg)

          // Buscar papel na organização ativa
          if (activeOrg.ownerId === authUser.uid) {
            setUserRole('owner')
          } else {
            const memberDoc = membersSnapshot.docs.find(d => d.data().orgId === activeOrg.id)
            setUserRole(memberDoc?.data().role || 'member')
          }
        } else {
          setOrganizations([])
          setCurrentOrg(null)
          setUserRole(null)
        }
      } else {
        setUser(null)
        setUserData(null)
        setUserRole(null)
        setOrganizations([])
        setCurrentOrg(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <NotificationProvider>
      <Toaster position="top-right" />
      <ReloadPrompt />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Onboarding Flow */}
        <Route path="/onboarding" element={
          <AuthGuard>
            <OnboardingPage />
          </AuthGuard>
        } />
        <Route path="/create-org" element={
          <AuthGuard>
            <CreateOrgPage />
          </AuthGuard>
        } />
        <Route path="/join-org" element={
          <AuthGuard>
            <JoinOrgPage />
          </AuthGuard>
        } />

        <Route element={<Layout />}>
          <Route path="dashboard" element={
            currentOrg ? <DashboardPage /> : <Navigate to="/onboarding" replace />
          } />
          <Route path="spaces" element={
            currentOrg ? <SpacesPage /> : <Navigate to="/onboarding" replace />
          } />
          <Route path="bookings" element={
            currentOrg ? <BookingsPage /> : <Navigate to="/onboarding" replace />
          } />
          <Route path="bookings/:id" element={
            currentOrg ? <BookingDetailsPage /> : <Navigate to="/onboarding" replace />
          } />
          <Route path="bookings/catalog" element={
            currentOrg ? <BookingCatalog /> : <Navigate to="/onboarding" replace />
          } />
          <Route path="bookings/search" element={
            currentOrg ? <BookingFilterPage /> : <Navigate to="/onboarding" replace />
          } />

          {/* Nova Rota Unificada */}
          <Route path="reserve" element={
            currentOrg ? <BookingUnifiedPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="bookings/new/:spaceId" element={
            currentOrg ? <NewBookingPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="approvals" element={
            currentOrg ? <ApprovalQueuePage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="admin/bookings" element={
            currentOrg ? <AdminBookingsPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="admin/payments" element={
            currentOrg ? <PaymentQueuePage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="admin" element={
            currentOrg ? <AdminPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="members" element={
            currentOrg ? <MembersPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="reports" element={
            currentOrg ? <ReportsPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="financial" element={
            currentOrg ? <FinancialDashboardPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="upgrade-plan" element={
            currentOrg ? <UpgradePlanPage /> : <Navigate to="/onboarding" replace />
          } />


          <Route path="communication" element={
            currentOrg ? <CommunicationPage /> : <Navigate to="/onboarding" replace />
          } />

          <Route path="profile" element={<UserProfilePage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </NotificationProvider>
  )
}
